import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    description: '',
    website_url: '', // Honeypot antispam-felt
  });

  const [status, setStatus] = useState({
    type: '', // 'success', 'error', 'loading'
    message: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: 'loading', message: 'Sender din besked...' });

    // Validering
    if (!formData.name.trim() || !formData.phone.trim() || !formData.email.trim() || !formData.description.trim()) {
      setStatus({ type: 'error', message: 'Udfyld venligst alle påkrævede felter.' });
      return;
    }

    try {
      const res = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          description: formData.description,
          website_url: formData.website_url, // Send honeypot med
        }),
      });

      const data = await res.json();

      if (res.ok && data.status === 'ok') {
        setStatus({
          type: 'success',
          message: 'Tak for din henvendelse! Vi har modtaget din besked og vender tilbage til dig hurtigst muligt.',
        });
        setFormData({
          name: '',
          phone: '',
          email: '',
          description: '',
          website_url: '',
        });
      } else {
        setStatus({
          type: 'error',
          message: data.message || 'Der skete en fejl under afsendelsen. Prøv venligst igen eller ring til os direkte.',
        });
      }
    } catch (err) {
      console.error('Form submit error:', err);
      setStatus({
        type: 'error',
        message: 'Kunne ikke oprette forbindelse til serveren. Prøv venligst igen senere eller ring til os.',
      });
    }
  };

  return (
    <>
      {/* React 19 Native Metadata Hoisting */}
      <title>Kontakt - Nygaards Vinduespolering</title>
      <meta
        name="description"
        content="Kontakt Nygaards Vinduespolering i Kerteminde for et uforpligtende tilbud på vinduespolering eller tagrende rens."
      />

      {/* Hero Header */}
      <section className="bg-slate-50/70 py-20 border-b border-slate-100">
        <div className="mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-orange">
            Få et uforpligtende tilbud
          </span>
          <h1 className="mt-3 font-display text-3xl font-bold uppercase tracking-wider text-brand-blue sm:text-4xl">
            Kontakt os
          </h1>
          <div className="mx-auto mt-4 h-0.5 w-12 bg-brand-orange" />
          <p className="mx-auto mt-6 max-w-2xl text-sm font-light leading-relaxed text-slate-500">
            Vi står klar til at hjælpe dig med rene vinduer og tagrender. Ring, send en mail eller udfyld formularen nedenfor.
          </p>
        </div>
      </section>

      {/* Kontakt Sektion */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
            {/* Kontaktoplysninger */}
            <div className="lg:col-span-5">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-orange">
                Virksomhed
              </span>
              <h2 className="mt-2 font-display text-2xl font-bold uppercase tracking-wider text-brand-dark">
                Nygaards Vinduespolering
              </h2>
              <p className="mt-6 text-sm font-light leading-relaxed text-slate-500">
                Vi kører ud til både private og erhvervskunder i hele Kerteminde og omegn. Ingen opgave er for lille eller for stor.
              </p>

              <div className="mt-8 space-y-6">
                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-none bg-brand-light-blue text-brand-blue">
                    <i className="fa-solid fa-location-dot text-lg" />
                  </div>
                  <div>
                    <h3 className="font-display text-xs font-bold uppercase tracking-wider text-brand-dark">Adresse</h3>
                    <p className="mt-1 text-xs font-light text-slate-500">
                      Tværgyden 151<br />
                      5300 Kerteminde
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-none bg-brand-light-blue text-brand-blue">
                    <i className="fa-solid fa-phone text-lg" />
                  </div>
                  <div>
                    <h3 className="font-display text-xs font-bold uppercase tracking-wider text-brand-dark">Telefon</h3>
                    <p className="mt-1 text-xs font-light text-slate-500">
                      <a href="tel:27501226" className="hover:text-brand-blue transition-colors whitespace-nowrap">
                        27 50 12 26
                      </a>
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-none bg-brand-light-blue text-brand-blue">
                    <i className="fa-solid fa-envelope text-lg" />
                  </div>
                  <div>
                    <h3 className="font-display text-xs font-bold uppercase tracking-wider text-brand-dark">E-mail</h3>
                    <p className="mt-1 text-xs font-light text-slate-500">
                      <a href="mailto:nygaardspolering@gmail.com" className="hover:text-brand-blue transition-colors break-all sm:break-normal">
                        nygaardspolering@gmail.com
                      </a>
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-none bg-brand-light-blue text-brand-blue">
                    <i className="fa-solid fa-briefcase text-lg" />
                  </div>
                  <div>
                    <h3 className="font-display text-xs font-bold uppercase tracking-wider text-brand-dark">CVR-nummer</h3>
                    <p className="mt-1 text-xs font-light text-slate-500 font-mono">
                      40339604
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Kontaktformular */}
            <div className="lg:col-span-7">
              <div className="rounded-none border border-slate-200 bg-slate-50/50 p-6 sm:p-10 shadow-none">
                <h3 className="font-display text-lg font-bold uppercase tracking-wider text-brand-dark">
                  Send os en besked
                </h3>
                <p className="mt-2 text-xs font-light text-slate-500">
                  Udfyld formularen, så vender vi tilbage med et uforpligtende tilbud inden for 24 timer.
                </p>

                <form onSubmit={handleSubmit} className="mt-8 space-y-6">
                  {/* Honeypot Antispam Felt (Absolut skjult for mennesker) */}
                  <div className="sr-only absolute -z-50 overflow-hidden h-0 w-0">
                    <label htmlFor="website_url">Honeypot - udfyld ikke dette felt</label>
                    <input
                      id="website_url"
                      type="text"
                      name="website_url"
                      value={formData.website_url}
                      onChange={handleChange}
                      tabIndex="-1"
                      autoComplete="off"
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    {/* Navn */}
                    <div>
                      <label htmlFor="name" className="block text-xs font-bold uppercase tracking-wider text-slate-700">
                        Navn <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="name"
                        type="text"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="mt-2 w-full rounded-none border border-slate-200 bg-white px-4 py-3 text-xs text-slate-800 focus:border-brand-blue focus:outline-none focus:ring-1 focus:ring-brand-blue/20"
                        placeholder="Dit fulde navn"
                      />
                    </div>

                    {/* Telefonnummer */}
                    <div>
                      <label htmlFor="phone" className="block text-xs font-bold uppercase tracking-wider text-slate-700">
                        Telefonnummer <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="phone"
                        type="tel"
                        name="phone"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        className="mt-2 w-full rounded-none border border-slate-200 bg-white px-4 py-3 text-xs text-slate-800 focus:border-brand-blue focus:outline-none focus:ring-1 focus:ring-brand-blue/20"
                        placeholder="Dit telefonnummer"
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="email" className="block text-xs font-bold uppercase tracking-wider text-slate-700">
                      E-mailadresse <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="email"
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="mt-2 w-full rounded-none border border-slate-200 bg-white px-4 py-3 text-xs text-slate-800 focus:border-brand-blue focus:outline-none focus:ring-1 focus:ring-brand-blue/20"
                      placeholder="din.email@eksempel.dk"
                    />
                  </div>

                  {/* Beskrivelse */}
                  <div>
                    <label htmlFor="description" className="block text-xs font-bold uppercase tracking-wider text-slate-700">
                      Beskrivelse af opgaven <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      required
                      rows="4"
                      value={formData.description}
                      onChange={handleChange}
                      className="mt-2 w-full rounded-none border border-slate-200 bg-white px-4 py-3 text-xs text-slate-800 focus:border-brand-blue focus:outline-none focus:ring-1 focus:ring-brand-blue/20"
                      placeholder="Beskriv opgaven (f.eks. antal vinduer, etager, tagrendens længde osv.)"
                    />
                  </div>

                  {/* Statusmeddelelser */}
                  {status.message && (
                    <div
                      className={`rounded-none p-4 text-xs font-medium ${
                        status.type === 'success'
                          ? 'bg-emerald-50 text-emerald-800 border border-emerald-100'
                          : status.type === 'error'
                          ? 'bg-red-50 text-red-800 border border-red-100'
                          : 'bg-blue-50 text-blue-800 border border-blue-100'
                      }`}
                    >
                      {status.type === 'loading' && (
                        <i className="fa-solid fa-spinner animate-spin mr-2" />
                      )}
                      {status.message}
                    </div>
                  )}

                  {/* Send-knap */}
                  <button
                    type="submit"
                    disabled={status.type === 'loading'}
                    className="w-full rounded-full bg-brand-blue py-3.5 font-display text-xs font-bold uppercase tracking-[0.2em] text-white shadow-none hover:bg-brand-orange transition-all duration-300 hover:scale-105 disabled:opacity-50"
                  >
                    {status.type === 'loading' ? 'Sender...' : 'Send besked'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
